import React, { useEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import { waUrl } from "../../config/bdk";
import MessageList from "./MessageList";
import ChatInput from "./ChatInput";

const WELCOME_MESSAGE =
  "¡Hola! Soy NEXO, tu asistente.\n" +
  "Puedo resolver tus dudas y orientarte sobre qué tipo de página necesita tu negocio.\n" +
  "¿Qué tienes en mente?";

// Deben mantenerse en sync con FALLBACK_MESSAGE / MESSAGE_CAP_MESSAGE en
// backend/nexo/router.py. El backend marca show_whatsapp_handoff=true en
// estos dos casos aunque el "último mensaje del asistente" no sea un
// resumen real de la conversación (es un mensaje de error o de límite) —
// si se usan tal cual como "resumen" en el link de WhatsApp, se filtra un
// mensaje de sistema como si fuera contenido de la charla.
const FALLBACK_MESSAGE =
  "Tuvimos un problema técnico y no pude procesar tu mensaje. " +
  "Puedes intentarlo de nuevo en un momento, o escribirnos directo por " +
  "WhatsApp mientras tanto.";

const MESSAGE_CAP_MESSAGE =
  "Llegamos al máximo de mensajes de esta conversación. Escríbenos por " +
  "WhatsApp o el formulario de contacto para continuar.";

const SYSTEM_MESSAGES = new Set([FALLBACK_MESSAGE, MESSAGE_CAP_MESSAGE]);

const ChatPanel = ({
  onClose,
  messages,
  sending,
  error,
  conversationClosed,
  showWhatsappHandoff,
  sendMessage,
  retry,
  resetConversation,
}) => {
  const [draft, setDraft] = useState("");
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages.length, sending]);

  const lastAssistantMessage = [...messages]
    .reverse()
    .find((m) => m.role === "assistant")?.content;

  const usableSummary =
    lastAssistantMessage && !SYSTEM_MESSAGES.has(lastAssistantMessage)
      ? lastAssistantMessage
      : undefined;

  const handoffWhatsappHref = waUrl(
    usableSummary
      ? `Vengo del chat de NEXO en la página. Esto es lo que platicamos: ${usableSummary}`
      : undefined
  );

  const onSubmit = (e) => {
    e.preventDefault();
    if (!draft.trim() || sending || conversationClosed) return;
    sendMessage(draft);
    setDraft("");
  };

  return (
    <div
      data-testid="nexo-chat-panel"
      className="fixed z-40 flex flex-col w-[400px] max-w-[94vw] h-[75vh] max-h-[640px] rounded-[14px] overflow-hidden"
      style={{
        right: "22px",
        bottom: "90px",
        background: "var(--ink)",
        border: "1px solid var(--ink-2)",
        boxShadow: "0 20px 60px rgba(0,0,0,.45)",
      }}
    >
      <div
        className="flex items-center justify-between px-4 py-3"
        style={{ borderBottom: "1px solid var(--ink-2)" }}
      >
        <div className="flex items-center gap-2.5">
          <img
            src={`${process.env.PUBLIC_URL}/img/NEXO-PERFIL.jpg`}
            alt="NEXO"
            className="w-9 h-9 rounded-full object-cover"
            style={{ border: "1px solid var(--red)" }}
          />
          <div>
            <p className="kicker" style={{ color: "var(--red)", marginBottom: 0 }}>
              NEXO
            </p>
            <p className="text-white text-[13px] font-bold -mt-1.5">
              Asistente de CREATIVOS BDK
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          data-testid="nexo-chat-close"
          aria-label="Cerrar chat"
          className="transition-colors"
          style={{ color: "#b8b9c1" }}
        >
          <X size={20} />
        </button>
      </div>

      <MessageList
        messages={messages}
        sending={sending}
        welcomeMessage={WELCOME_MESSAGE}
        bottomRef={bottomRef}
      />

      {error && (
        <div
          data-testid="nexo-chat-error"
          className="px-4 py-2 text-[12px]"
          style={{ color: "#ff8080", borderTop: "1px solid var(--ink-2)" }}
        >
          {error}{" "}
          <button
            type="button"
            onClick={retry}
            data-testid="nexo-chat-retry"
            className="underline font-bold"
            style={{ color: "#fff" }}
          >
            Reintentar
          </button>
        </div>
      )}

      {showWhatsappHandoff && (
        <div className="px-4 py-2" style={{ borderTop: "1px solid var(--ink-2)" }}>
          <a
            href={handoffWhatsappHref}
            target="_blank"
            rel="noreferrer"
            data-testid="nexo-whatsapp-handoff-link"
            className="bdk-btn bdk-btn--whatsapp w-full justify-center text-[13px] py-2.5"
          >
            Continuar por WhatsApp
          </a>
        </div>
      )}

      <div className="px-4 py-3" style={{ borderTop: "1px solid var(--ink-2)" }}>
        {conversationClosed && (
          <button
            type="button"
            onClick={resetConversation}
            data-testid="nexo-chat-restart"
            className="bdk-btn bdk-btn--ghost-dark w-full justify-center text-[12px] py-2 mb-2.5"
          >
            Iniciar una nueva conversación
          </button>
        )}
        <ChatInput
          value={draft}
          onChange={setDraft}
          onSubmit={onSubmit}
          disabled={conversationClosed}
          sending={sending}
        />
        <a
          href={waUrl()}
          target="_blank"
          rel="noreferrer"
          data-testid="nexo-whatsapp-direct-link"
          className="block text-center text-[11px] mt-2 underline"
          style={{ color: "#8a8b93" }}
        >
          Prefiero hablar por WhatsApp directo
        </a>
      </div>
    </div>
  );
};

export default ChatPanel;
